
import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export type Organization = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  color_scheme: 'default' | 'blue' | 'green' | 'purple' | 'dark';
  role: string;
};

type OrganizationContextType = {
  organizations: Organization[];
  currentOrganization: Organization | null;
  isLoading: boolean;
  switchOrganization: (organizationId: string) => Promise<void>;
  createOrganization: (data: { name: string; slug: string }) => Promise<void>;
  updateOrganization: (id: string, data: Partial<Organization>) => Promise<void>;
  uploadLogo: (id: string, file: File) => Promise<string>;
  inviteUser: (organizationId: string, email: string, role: string) => Promise<void>;
};

const OrganizationContext = createContext<OrganizationContextType>({
  organizations: [],
  currentOrganization: null,
  isLoading: true,
  switchOrganization: async () => {},
  createOrganization: async () => {},
  updateOrganization: async () => {},
  uploadLogo: async () => '',
  inviteUser: async () => {},
});

export const useOrganization = () => useContext(OrganizationContext);

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { session } = useAuth();
  const { toast } = useToast();
  
  const organizationsCache = useRef<{ [userId: string]: Organization[] }>({});
  const lastFetchTime = useRef<{ [userId: string]: number }>({});
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const isInitialized = useRef(false);
  const isTabVisible = useRef(true);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Load cached data from localStorage on mount
  useEffect(() => {
    if (isInitialized.current) return;
    
    // Set up visibility change listener
    const handleVisibilityChange = () => {
      isTabVisible.current = !document.hidden;
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const savedOrgs = localStorage.getItem('organizations');
    const savedCurrentOrg = localStorage.getItem('currentOrganization');
    
    if (savedOrgs) {
      try {
        const parsedOrgs = JSON.parse(savedOrgs);
        setOrganizations(parsedOrgs);
        organizationsCache.current[session?.user?.id || 'temp'] = parsedOrgs;
      } catch (e) {
        console.error('Error parsing saved organizations:', e);
      }
    }
    
    if (savedCurrentOrg) {
      try {
        const parsedCurrentOrg = JSON.parse(savedCurrentOrg);
        setCurrentOrganization(parsedCurrentOrg);
      } catch (e) {
        console.error('Error parsing saved current organization:', e);
      }
    }
    
    isInitialized.current = true;
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const fetchOrganizations = useCallback(async (forceRefresh = false) => {
    // Skip if tab is not visible and not forcing refresh
    if (!isTabVisible.current && !forceRefresh) {
      console.log('⏸️ OrganizationContext: Skipping fetch - tab not visible');
      return;
    }
    
    console.log('🔄 OrganizationContext: Starting fetchOrganizations', { hasSession: !!session?.user, userId: session?.user?.id });
    
    if (!session?.user) {
      console.log('❌ OrganizationContext: No session, clearing state');
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
      return;
    }

    const userId = session.user.id;
    const now = Date.now();
    
    // Check cache first (unless forcing refresh)
    if (!forceRefresh && organizationsCache.current[userId] && lastFetchTime.current[userId]) {
      const timeSinceLastFetch = now - lastFetchTime.current[userId];
      if (timeSinceLastFetch < CACHE_DURATION) {
        console.log('📦 OrganizationContext: Using cached data');
        setOrganizations(organizationsCache.current[userId]);
        setIsLoading(false);
        return;
      }
    }
    
    try {
      setIsLoading(true);
      console.log('🔍 OrganizationContext: Fetching organizations for user:', userId);
      
      const { data, error } = await supabase.rpc('get_user_organizations', {
        user_id: userId
      });
      
      if (error) throw error;
      
      console.log('📊 OrganizationContext: Organizations fetched:', { count: data?.length, organizations: data });
      
      if (data && data.length > 0) {
        setOrganizations(data);
        organizationsCache.current[userId] = data;
        lastFetchTime.current[userId] = now;
        localStorage.setItem('organizations', JSON.stringify(data));
        
        // Check if user has a current organization set
        const { data: profile } = await supabase
          .from('profiles')
          .select('current_organization_id')
          .eq('id', userId)
          .maybeSingle();
        
        console.log('👤 OrganizationContext: Profile data:', profile);
        
        // If user has a current organization, set it as active
        if (profile?.current_organization_id) {
          const currentOrg = data.find(org => org.id === profile.current_organization_id);
          if (currentOrg) {
            console.log('✅ OrganizationContext: Setting current organization from profile:', currentOrg.name);
            setCurrentOrganization(currentOrg);
            localStorage.setItem('currentOrganization', JSON.stringify(currentOrg));
          } else {
            // If the stored org ID doesn't exist in user's orgs, set the first one
            console.log('⚠️ OrganizationContext: Stored org not found, setting first organization:', data[0].name);
            setCurrentOrganization(data[0]);
            localStorage.setItem('currentOrganization', JSON.stringify(data[0]));
            await supabase
              .from('profiles')
              .update({ current_organization_id: data[0].id })
              .eq('id', userId);
          }
        } else {
          // If no current organization, set the first one
          console.log('🆕 OrganizationContext: No current org in profile, setting first organization:', data[0].name);
          setCurrentOrganization(data[0]);
          localStorage.setItem('currentOrganization', JSON.stringify(data[0]));
          await supabase
            .from('profiles')
            .update({ current_organization_id: data[0].id })
            .eq('id', userId);
        }
      } else {
        console.log('📭 OrganizationContext: No organizations found for user');
        setOrganizations([]);
        setCurrentOrganization(null);
        localStorage.removeItem('organizations');
        localStorage.removeItem('currentOrganization');
      }
    } catch (error) {
      console.error('💥 OrganizationContext: Error fetching organizations:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to load organizations',
        description: error instanceof Error ? error.message : 'An error occurred'
      });
    } finally {
      console.log('🏁 OrganizationContext: fetchOrganizations complete, isLoading = false');
      setIsLoading(false);
    }
  }, [session?.user, toast]);

  // Single effect to handle session changes with debouncing
  useEffect(() => {
    if (!isInitialized.current) return;
    
    console.log('🔄 OrganizationContext: Effect triggered', { 
      hasSession: !!session?.user?.id, 
      userId: session?.user?.id,
      sessionUser: session?.user 
    });
    
    // Clear any pending debounced fetch
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // Only fetch if session exists and is stable
    if (session?.user?.id) {
      console.log('⏰ OrganizationContext: Setting debounced timeout to fetch organizations');
      debounceTimeout.current = setTimeout(() => {
        fetchOrganizations();
      }, 200);
      
      return () => {
        if (debounceTimeout.current) {
          console.log('🧹 OrganizationContext: Clearing debounced timeout');
          clearTimeout(debounceTimeout.current);
        }
      };
    } else {
      console.log('❌ OrganizationContext: No session in effect, clearing state');
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
      localStorage.removeItem('organizations');
      localStorage.removeItem('currentOrganization');
    }
  }, [session?.user?.id, fetchOrganizations]);

  // Auto-select first organization when data is loaded (with debouncing)
  useEffect(() => {
    if (!isLoading && !currentOrganization && organizations.length > 0 && session?.user && isTabVisible.current) {
      console.log('🔄 OrganizationContext: Auto-selecting first organization:', organizations[0].name);
      
      // Use sessionStorage for this temporary state to avoid persistence across sessions
      const hasAutoSelected = sessionStorage.getItem('hasAutoSelectedOrg');
      if (!hasAutoSelected) {
        switchOrganization(organizations[0].id);
        sessionStorage.setItem('hasAutoSelectedOrg', 'true');
      }
    }
  }, [isLoading, currentOrganization, organizations, session?.user]);

  const switchOrganization = useCallback(async (organizationId: string) => {
    if (!session?.user) return;
    
    try {
      const org = organizations.find(o => o.id === organizationId);
      if (!org) {
        throw new Error('Organization not found');
      }

      await supabase
        .from('profiles')
        .update({ current_organization_id: organizationId })
        .eq('id', session.user.id);
      
      setCurrentOrganization(org);
      localStorage.setItem('currentOrganization', JSON.stringify(org));
      
      toast({
        title: 'Organization Switched',
        description: `You are now working in ${org.name}`
      });
      
    } catch (error) {
      console.error('Error switching organizations:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to switch organization',
        description: error instanceof Error ? error.message : 'An error occurred'
      });
    }
  }, [organizations, session?.user, toast]);

  const createOrganization = useCallback(async (data: { name: string; slug: string }) => {
    if (!session?.user) return;
    
    try {
      // Insert new organization
      const { data: newOrg, error } = await supabase
        .from('organizations')
        .insert({
          name: data.name,
          slug: data.slug,
          created_by: session.user.id
        })
        .select()
        .single();
      
      if (error) throw error;
      if (!newOrg) throw new Error('Failed to create organization');
      
      // Add creator as admin member
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: newOrg.id,
          user_id: session.user.id,
          role: 'admin'
        });
      
      if (memberError) throw memberError;
      
      // Refresh organizations list
      await fetchOrganizations(true);
      
      // Switch to the new organization
      await switchOrganization(newOrg.id);
      
      toast({
        title: 'Organization Created',
        description: `${newOrg.name} has been created successfully`
      });
      
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create organization',
        description: error instanceof Error ? error.message : 'An error occurred'
      });
    }
  }, [session?.user, fetchOrganizations, switchOrganization, toast]);

  const updateOrganization = useCallback(async (id: string, data: Partial<Organization>) => {
    try {
      const { error } = await supabase
        .from('organizations')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
      
      // Refresh organizations list
      await fetchOrganizations(true);
      
      toast({
        title: 'Organization Updated',
        description: 'Organization settings have been updated'
      });
      
    } catch (error) {
      console.error('Error updating organization:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update organization',
        description: error instanceof Error ? error.message : 'An error occurred'
      });
    }
  }, [fetchOrganizations, toast]);

  const uploadLogo = useCallback(async (id: string, file: File): Promise<string> => {
    try {
      // Create a storage bucket if it doesn't exist
      const { data: bucketData, error: bucketError } = await supabase.storage.createBucket('organization-logos', {
        public: true,
        fileSizeLimit: 1024 * 1024 * 2, // 2MB
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml']
      });
      
      // Ignore error if bucket already exists
      if (bucketError && bucketError.message !== 'Bucket already exists') {
        throw bucketError;
      }
      
      // Upload file
      const fileExt = file.name.split('.').pop();
      const fileName = `${id}-${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('organization-logos')
        .upload(fileName, file, {
          upsert: true,
          cacheControl: '3600'
        });
      
      if (error) throw error;
      
      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('organization-logos')
        .getPublicUrl(fileName);
      
      const logoUrl = publicUrlData.publicUrl;
      
      // Update organization with logo URL
      await updateOrganization(id, { logo_url: logoUrl });
      
      return logoUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        variant: 'destructive',
        title: 'Logo Upload Failed',
        description: error instanceof Error ? error.message : 'Failed to upload logo'
      });
      return '';
    }
  }, [updateOrganization, toast]);

  const inviteUser = useCallback(async (organizationId: string, email: string, role: string) => {
    try {
      // This is a simplified implementation. In a real app, you would:
      // 1. Create a row in an invitations table
      // 2. Send an email with a signup link
      // 3. When the user signs up, check for invitations
      
      // For now, we'll assume the user already exists
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();
      
      if (userError) {
        if (userError.code === 'PGRST116') {
          throw new Error(`User with email ${email} not found`);
        }
        throw userError;
      }
      
      // Add user to organization
      const { error } = await supabase
        .from('organization_members')
        .insert({
          organization_id: organizationId,
          user_id: userData.id,
          role
        });
      
      if (error) throw error;
      
      toast({
        title: 'User Invited',
        description: `${email} has been added to the organization`
      });
      
    } catch (error) {
      console.error('Error inviting user:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to invite user',
        description: error instanceof Error ? error.message : 'An error occurred'
      });
    }
  }, [toast]);

  const contextValue = useMemo(() => ({
    organizations,
    currentOrganization,
    isLoading,
    switchOrganization,
    createOrganization,
    updateOrganization,
    uploadLogo,
    inviteUser
  }), [
    organizations,
    currentOrganization,
    isLoading,
    switchOrganization,
    createOrganization,
    updateOrganization,
    uploadLogo,
    inviteUser
  ]);

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};
